import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { safeGet, safeRemove, safeSet } from "@/lib/safeStorage";
import { devLog } from "@/lib/devLog";

const ScannerCtx = createContext(null);

// Persisted user preference (non-sensitive): remembers whether the global
// keyboard-wedge listener should be armed after a page refresh.
const STORAGE_KEY = "bakery_scanner_enabled";

export function ScannerProvider({ children }) {
  const [enabled, setEnabled] = useState(() => safeGet(STORAGE_KEY) === "1");
  const [lastScans, setLastScans] = useState([]); // [{code, ts}]
  const [bleStatus, setBleStatus] = useState("idle"); // idle | connecting | connected | unsupported | error
  const subscribers = useRef(new Set());
  const buf = useRef("");
  const timer = useRef(null);

  const emit = useCallback((code) => {
    const norm = String(code || "").trim();
    if (!norm || norm.length < 3) return;
    setLastScans((prev) => [{ code: norm, ts: Date.now() }, ...prev].slice(0, 20));
    subscribers.current.forEach((fn) => {
      try {
        fn(norm);
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          devLog("Scanner subscriber threw", err);
        }
      }
    });
  }, []);

  // Global keyboard-wedge listener — only active when enabled.
  useEffect(() => {
    if (!enabled) return undefined;
    const onKey = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select") return;
      if (e.key === "Enter") {
        const code = buf.current.trim();
        buf.current = "";
        if (code.length >= 3) emit(code);
        return;
      }
      if (e.key.length === 1) {
        if (timer.current) clearTimeout(timer.current);
        buf.current += e.key;
        // Reset if no follow-up key for 250 ms (human typing).
        timer.current = setTimeout(() => {
          buf.current = "";
        }, 250);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      if (timer.current) clearTimeout(timer.current);
    };
  }, [enabled, emit]);

  const enable = useCallback(() => {
    safeSet(STORAGE_KEY, "1");
    setEnabled(true);
  }, []);
  const disable = useCallback(() => {
    safeRemove(STORAGE_KEY);
    setEnabled(false);
  }, []);

  const subscribe = useCallback((fn) => {
    subscribers.current.add(fn);
    return () => subscribers.current.delete(fn);
  }, []);

  // Optional Web Bluetooth attempt — works only for BLE scanners that expose a
  // standard battery / HID-over-GATT-style service. Most retail Bluetooth
  // scanners pair as plain HID at the OS level, in which case the keyboard
  // wedge above is what does the actual work.
  const tryBle = useCallback(async () => {
    if (!("bluetooth" in navigator)) {
      setBleStatus("unsupported");
      return { ok: false, reason: "Web Bluetooth not supported in this browser" };
    }
    setBleStatus("connecting");
    try {
      const device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: ["battery_service", "device_information"],
      });
      device.addEventListener("gattserverdisconnected", () => setBleStatus("idle"));
      await device.gatt?.connect();
      setBleStatus("connected");
      enable();
      return { ok: true, name: device.name || "Unnamed device" };
    } catch (err) {
      setBleStatus("error");
      return { ok: false, reason: err?.message || "Connection cancelled" };
    }
  }, [enable]);

  const value = useMemo(
    () => ({
      enabled,
      lastScans,
      bleStatus,
      enable,
      disable,
      subscribe,
      emit,
      tryBle,
    }),
    [enabled, lastScans, bleStatus, enable, disable, subscribe, emit, tryBle]
  );

  return <ScannerCtx.Provider value={value}>{children}</ScannerCtx.Provider>;
}

export const useScanner = () => useContext(ScannerCtx);
